import { Button, ActionIcon, Menu, Group, useMantineColorScheme, Tooltip, Text, rem, Stack } from "@mantine/core";
import { Settings, ChevronDown, RefreshCw, Clock, Check, Info, Maximize, Minimize, Pencil, Trash, Activity } from "lucide-react";
import * as React from "react";
import { useDisclosure } from "@mantine/hooks";
import { useAuth } from "@bi-studio/shared/hooks";
import { useNavigate } from "react-router-dom";
import { ConfirmationModal } from "@bi-studio/shared/components";
import { deleteDashboard, getFolderForItem } from "@bi-studio/shared/apis";

export interface DashboardActionsButtonProps {
    dashboardId: string;
    handleRefresh: () => void;
    isRendering: boolean;
    isLoading: boolean;
    autoRefreshEnabled: boolean;
    setAutoRefreshEnabled: (enabled: boolean) => void;
    handleViewDetails: () => void;
    handleFullscreenToggle: () => void;
    isFullscreen: boolean;
    onEdit?: () => void;
    lastRefreshDate: Date | null;
}

export function DashboardActionsButton({
    dashboardId,
    handleRefresh,
    isRendering,
    isLoading,
    autoRefreshEnabled,
    setAutoRefreshEnabled,
    handleViewDetails,
    handleFullscreenToggle,
    isFullscreen,
    onEdit,
    lastRefreshDate
}: DashboardActionsButtonProps) {
    const { colorScheme } = useMantineColorScheme();
    const { token } = useAuth();
    const navigate = useNavigate();
    const [deleteOpened, { open: openDelete, close: closeDelete }] = useDisclosure(false);
    const [isDeleting, setIsDeleting] = React.useState(false);

    const handleConfirmDelete = async () => {
        if (!token || !dashboardId) return;
        try {
            setIsDeleting(true);
            const folderInfo = await getFolderForItem(token, "dashboard", dashboardId);
            await deleteDashboard(token, dashboardId);
            closeDelete();
            window.dispatchEvent(new CustomEvent("refresh-folders"));
            if (folderInfo.folderId) {
                navigate(`/folders/${folderInfo.folderId}`);
            } else {
                navigate("/folders");
            }
        } catch (error) {
            console.error("Failed to delete dashboard:", error);
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <Group gap="md">
            {lastRefreshDate && (
                <Stack gap={2} align="flex-end">
                    <Group gap={4} align="center">
                        {autoRefreshEnabled && (
                            <Activity size={14} color="var(--mantine-color-green-6)" className="animate-pulse" />
                        )}
                        <Text size="xs" c="dimmed" fw={500} style={{ textTransform: 'uppercase', letterSpacing: rem(0.5) }}>
                            Last Refresh
                        </Text>
                    </Group>
                    <Text size="sm" fw={600}>
                        {lastRefreshDate.toLocaleTimeString()}
                    </Text>
                </Stack>
            )}



            <Menu shadow="md" width={220} position="bottom-end">
                <Menu.Target>
                    <Group gap={0}>
                        <Button
                            visibleFrom="sm"
                            color="black"
                            c="white"
                            leftSection={<Settings size={16} />}
                            rightSection={<ChevronDown size={14} />}
                        >
                            Actions
                        </Button>
                        <Tooltip label="Actions">
                            <ActionIcon
                                hiddenFrom="sm"
                                variant="filled"
                                color="black"
                                c="white"
                                size="lg"
                            >
                                <Settings size={20} />
                            </ActionIcon>
                        </Tooltip>
                    </Group>
                </Menu.Target>

                <Menu.Dropdown
                    style={{
                        backgroundColor: colorScheme === 'dark' ? 'var(--modal-bg)' : undefined,
                        borderColor: colorScheme === 'dark' ? 'var(--modal-border)' : undefined,
                        backdropFilter: 'blur(var(--modal-blur))'
                    }}
                >
                    <Menu.Item
                        leftSection={<RefreshCw size={16} />}
                        onClick={handleRefresh}
                        disabled={isRendering || isLoading}
                    >
                        Refresh
                    </Menu.Item>
                    <Menu.Item
                        leftSection={<Clock size={16} />}
                        rightSection={autoRefreshEnabled ? <Check size={14} /> : null}
                        onClick={() => setAutoRefreshEnabled(!autoRefreshEnabled)}
                    >
                        Auto-refresh (5m)
                    </Menu.Item>
                    <Menu.Item
                        leftSection={<Info size={16} />}
                        onClick={handleViewDetails}
                    >
                        View Details
                    </Menu.Item>
                    <Menu.Item
                        leftSection={isFullscreen ? <Minimize size={16} /> : <Maximize size={16} />}
                        onClick={handleFullscreenToggle}
                    >
                        {isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                    </Menu.Item>
                    {onEdit && <Menu.Divider />}
                    {onEdit && (
                        <Menu.Item
                            leftSection={<Pencil size={16} />}
                            onClick={onEdit}
                        >
                            Edit
                        </Menu.Item>
                    )}
                    <Menu.Item
                        c="red"
                        leftSection={<Trash size={16} />}
                        onClick={openDelete}
                    >
                        Delete
                    </Menu.Item>
                </Menu.Dropdown>
            </Menu>

            <ConfirmationModal
                opened={deleteOpened}
                onClose={closeDelete}
                onConfirm={handleConfirmDelete}
                title="Delete"
                confirmLabel="Delete"
                confirmColor="red"
                loading={isDeleting}
            >
                Are you sure you want to delete this dashboard? This action cannot be undone.
            </ConfirmationModal>
        </Group>
    );
}
